import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./AppointmentTimePicker.scss";

const DEFAULT_INTERVAL_MINUTES = 5;
const FALLBACK_DAY_START_MINUTES = 6 * 60;
const FALLBACK_DAY_END_MINUTES = 22 * 60;

function pad2(value) {
  return String(value).padStart(2, "0");
}

function minutesToValue(totalMinutes) {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${pad2(hour)}:${pad2(minute)}`;
}

/** Parse HH:mm / HH:mm:ss / h:mm AM|PM → minutes from midnight. */
export function parseClockToMinutes(value) {
  const text = String(value || "").trim();
  if (!text) return null;

  const twelve = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (twelve) {
    let hour = Number(twelve[1]);
    const minute = Number(twelve[2]);
    const ampm = twelve[4].toUpperCase();
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return hour * 60 + minute;
  }

  const twentyFour = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!twentyFour) return null;
  const hour = Number(twentyFour[1]);
  const minute = Number(twentyFour[2]);
  if (
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return hour * 60 + minute;
}

function valueToMinutes(value) {
  return parseClockToMinutes(value);
}

function formatDisplay(value) {
  const total = valueToMinutes(value);
  if (total == null) return "";
  const hour24 = Math.floor(total / 60);
  const minute = total % 60;
  const ampm = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${pad2(minute)} ${ampm}`;
}

/** Visit/org slot duration → usable minute step; blank/invalid → 5. */
export function resolveTimeIntervalMinutes(value, fallback = DEFAULT_INTERVAL_MINUTES) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return fallback;
  return Math.min(60, Math.max(1, Math.round(number)));
}

function resolveDayBounds(dayStartTime, dayEndTime, fullDay = false) {
  if (fullDay) return { start: 0, end: 24 * 60 };
  const start =
    parseClockToMinutes(dayStartTime) ?? FALLBACK_DAY_START_MINUTES;
  // Exclusive end matches org close time (legacy hour loop uses hour < MaxT).
  // Last selectable slot is the last interval that still starts before close.
  let end = parseClockToMinutes(dayEndTime) ?? FALLBACK_DAY_END_MINUTES;
  if (end <= start) {
    end = start;
  }
  return { start, end };
}

function slotPeriod(totalMinutes) {
  if (totalMinutes < 12 * 60) return "morning";
  if (totalMinutes < 17 * 60) return "afternoon";
  return "evening";
}

function buildSlots(intervalMinutes, dayStartTime, dayEndTime, fullDay = false) {
  const step = resolveTimeIntervalMinutes(intervalMinutes);
  const { start, end } = resolveDayBounds(dayStartTime, dayEndTime, fullDay);
  const slots = [];
  // Start inclusive, close exclusive — matches org open/close (legacy hour < MaxT).
  const lastStart = end > start ? end - step : start;
  for (let total = start; total <= lastStart; total += step) {
    const value = minutesToValue(total);
    slots.push({
      value,
      label: formatDisplay(value),
      period: slotPeriod(total),
      totalMinutes: total,
    });
  }
  return slots;
}

export function todayIsoDate() {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** Empty date is treated as today (same as legacy booking). */
function resolveEffectiveDateIso(appointmentDateIso) {
  const text = String(appointmentDateIso || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : todayIsoDate();
}

export function isPastSlot(value, appointmentDateIso) {
  const dateIso = resolveEffectiveDateIso(appointmentDateIso);
  const match = dateIso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const now = new Date();
  const selected = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    0,
    0,
    0,
  );
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0,
  );
  if (selected.getTime() < todayStart.getTime()) return true;
  const isToday = selected.getTime() === todayStart.getTime();
  if (!isToday) return false;
  const minutes = valueToMinutes(value);
  if (minutes == null) return false;
  return minutes <= now.getHours() * 60 + now.getMinutes();
}

/** True when a calendar/occupancy cell should not open Book (past day or past time). */
export function isPastBookableMoment(date, { allDay = false } = {}) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return true;
  const now = new Date();
  if (allDay) {
    const dayStart = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0,
    );
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0,
    );
    return dayStart.getTime() < todayStart.getTime();
  }
  return date.getTime() <= now.getTime();
}

/** Next selectable slot for date (defaults to today) within org/visit hours. */
export function getNextAvailableTime({
  appointmentDate = "",
  intervalMinutes = DEFAULT_INTERVAL_MINUTES,
  dayStartTime = "",
  dayEndTime = "",
} = {}) {
  const dateIso = resolveEffectiveDateIso(appointmentDate);
  const slots = buildSlots(intervalMinutes, dayStartTime, dayEndTime);
  const next = slots.find((slot) => !isPastSlot(slot.value, dateIso));
  return next?.value || "";
}

function localDateTimeMs(dateIso, timeValue) {
  const match = String(dateIso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const mins = valueToMinutes(timeValue);
  if (!match || mins == null) return NaN;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Math.floor(mins / 60),
    mins % 60,
    0,
    0,
  ).getTime();
}

function toRangeMs(range) {
  if (!range) return null;
  if (
    Number.isFinite(range.startMs) &&
    Number.isFinite(range.endMs) &&
    range.endMs > range.startMs
  ) {
    return { startMs: range.startMs, endMs: range.endMs };
  }
  const start =
    range.start instanceof Date
      ? range.start
      : range.startIso || range.start
        ? new Date(range.startIso || range.start)
        : null;
  const endRaw =
    range.end instanceof Date
      ? range.end
      : range.endIso || range.end
        ? new Date(range.endIso || range.end)
        : null;
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return null;
  const end =
    endRaw instanceof Date && !Number.isNaN(endRaw.getTime()) && endRaw > start
      ? endRaw
      : new Date(start.getTime() + DEFAULT_INTERVAL_MINUTES * 60 * 1000);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

/** True when [start, start+duration) overlaps any busy range on that date. */
export function isSlotOccupied({
  appointmentDate = "",
  time = "",
  durationMinutes = DEFAULT_INTERVAL_MINUTES,
  busyRanges = [],
} = {}) {
  const dateIso = resolveEffectiveDateIso(appointmentDate);
  const step = resolveTimeIntervalMinutes(durationMinutes);
  const startMs = localDateTimeMs(dateIso, time);
  if (Number.isNaN(startMs)) return false;
  const endMs = startMs + step * 60 * 1000;

  return (Array.isArray(busyRanges) ? busyRanges : []).some((range) => {
    const normalized = toRangeMs(range);
    if (!normalized) return false;
    return normalized.startMs < endMs && normalized.endMs > startMs;
  });
}

/**
 * Next free start time on/after `fromTime`, stepping by slot duration.
 * Slot duration drives both step and proposed booking length.
 */
export function nextFreeSlotTime({
  appointmentDate = "",
  fromTime = "",
  intervalMinutes = DEFAULT_INTERVAL_MINUTES,
  dayStartTime = "",
  dayEndTime = "",
  busyRanges = [],
} = {}) {
  const dateIso = resolveEffectiveDateIso(appointmentDate);
  const step = resolveTimeIntervalMinutes(intervalMinutes);
  const slots = buildSlots(step, dayStartTime, dayEndTime);
  const fromMins = valueToMinutes(fromTime);
  const candidates =
    fromMins == null
      ? slots
      : slots.filter((slot) => slot.totalMinutes >= fromMins);

  for (const slot of candidates) {
    if (isPastSlot(slot.value, dateIso)) continue;
    if (
      !isSlotOccupied({
        appointmentDate: dateIso,
        time: slot.value,
        durationMinutes: step,
        busyRanges,
      })
    ) {
      return slot.value;
    }
  }
  return "";
}

export default function AppointmentTimePicker({
  value = "",
  onChange,
  disabled = false,
  appointmentDate = "",
  intervalMinutes = DEFAULT_INTERVAL_MINUTES,
  dayStartTime = "",
  dayEndTime = "",
  placeholder = "Select time",
  ignorePast = false,
  /** When true with ignorePast=false, past slots are omitted (not just disabled). */
  hidePastSlots = false,
  fullDay = false,
  renderTrigger = null,
  "aria-label": ariaLabel = "Appointment time",
}) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("morning");
  const [panelStyle, setPanelStyle] = useState({});

  const step = useMemo(
    () => resolveTimeIntervalMinutes(intervalMinutes),
    [intervalMinutes],
  );

  const allSlots = useMemo(
    () => buildSlots(step, dayStartTime, dayEndTime, fullDay),
    [dayEndTime, dayStartTime, fullDay, step],
  );

  const slotsWithState = useMemo(
    () =>
      allSlots.map((slot) => ({
        ...slot,
        past: ignorePast ? false : isPastSlot(slot.value, appointmentDate),
      })),
    [allSlots, appointmentDate, ignorePast],
  );

  const periodSlots = useMemo(
    () =>
      slotsWithState.filter(
        (slot) =>
          slot.period === period && !(hidePastSlots && slot.past),
      ),
    [hidePastSlots, period, slotsWithState],
  );

  const displayValue = value ? formatDisplay(value) : "";

  useEffect(() => {
    if (!value) return;
    const slot = allSlots.find((row) => row.value === value);
    if (slot) setPeriod(slot.period);
  }, [allSlots, value]);

  useEffect(() => {
    if (!slotsWithState.length) return;
    if (slotsWithState.some((slot) => slot.period === period)) return;
    const firstSelectable =
      slotsWithState.find((slot) => !slot.past) || slotsWithState[0];
    setPeriod(firstSelectable.period);
  }, [period, slotsWithState]);

  useEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const place = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 280);
      const estimatedHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;
      const left = Math.min(
        Math.max(8, rect.left),
        window.innerWidth - width - 8,
      );
      setPanelStyle({
        position: "fixed",
        zIndex: 10050,
        width: `${width}px`,
        left: `${left}px`,
        ...(openUp
          ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: "auto" }
          : { top: `${rect.bottom + 6}px`, bottom: "auto" }),
      });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (
        panelRef.current?.contains(event.target) ||
        triggerRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // On open for today, jump to the first period that still has selectable times.
  useEffect(() => {
    if (!open) return;
    const selectable = slotsWithState.find((slot) => !slot.past);
    if (selectable) setPeriod(selectable.period);
  }, [open, slotsWithState]);

  const periods = [
    { key: "morning", label: "Morning" },
    { key: "afternoon", label: "Afternoon" },
    { key: "evening", label: "Evening" },
  ].filter((item) =>
    slotsWithState.some(
      (slot) =>
        slot.period === item.key && !(hidePastSlots && slot.past),
    ),
  );

  return (
    <div className="eplanner-time-picker">
      {typeof renderTrigger === "function" ? (
        renderTrigger({
          triggerRef,
          open,
          setOpen,
          disabled,
          displayValue,
          placeholder,
          ariaLabel,
        })
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={`form-control eplanner-time-picker__trigger${
            open ? " is-open" : ""
          }${displayValue ? "" : " is-placeholder"}`}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => {
            if (disabled) return;
            setOpen((current) => !current);
          }}
        >
          <span>{displayValue || placeholder}</span>
        </button>
      )}

      {open
        ? createPortal(
            <div
              ref={panelRef}
              className="eplanner-time-picker__panel"
              style={panelStyle}
              role="dialog"
              aria-label="Choose appointment time"
            >
              <div className="eplanner-time-picker__periods" role="tablist">
                {periods.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={period === item.key}
                    className={period === item.key ? "is-active" : ""}
                    onClick={() => setPeriod(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="eplanner-time-picker__grid">
                {periodSlots.length ? (
                  periodSlots.map((slot) => {
                    const selected = slot.value === value;
                    return (
                      <button
                        key={slot.value}
                        type="button"
                        disabled={slot.past}
                        title={slot.past ? "This time has passed" : slot.label}
                        className={`eplanner-time-picker__slot${
                          selected ? " is-selected" : ""
                        }${slot.past ? " is-past" : ""}`}
                        onClick={() => {
                          if (slot.past) return;
                          onChange?.(slot.value);
                          setOpen(false);
                        }}
                      >
                        {slot.label}
                      </button>
                    );
                  })
                ) : (
                  <div className="eplanner-time-picker__empty">
                    No times in this period for organisation hours.
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
